import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-neutral-50 py-12" data-testid="terms-conditions-page">
      <Helmet>
        <title>Terms & Conditions | Explore MHC - Martinsville & Henry County, VA</title>
        <meta name="description" content="Terms and conditions for using Explore MHC, the community platform for businesses and events in Martinsville and Henry County, Virginia." />
        <link rel="canonical" href="https://exploremhc.com/terms" />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-neutral-200">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Terms & Conditions
          </h1>
          <p className="text-neutral-500 mb-8">Last updated: March 6, 2026</p>

          <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">1. Acceptance of Terms</h2>
              <p>By accessing and using the Explore MHC website at exploremhc.com (the "Site"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree with any part of these Terms, you must not use the Site.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">2. Description of Service</h2>
              <p>Explore MHC is a community platform serving Martinsville and Henry County, Virginia. The Site provides:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>A business directory for local businesses</li>
                <li>An events calendar for community and business events</li>
                <li>Featured placement options for businesses and events</li>
                <li>A minority-owned business spotlight</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">3. User Accounts</h2>
              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Registration</h3>
              <p>To access certain features, you must register for an account. You agree to provide accurate, current, and complete information during registration and keep your account information up to date.</p>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Account Types</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Community Member:</strong> May submit events and purchase featured event placements</li>
                <li><strong>Business Owner:</strong> May manage one business listing, submit events, and purchase featured placements for both businesses and events</li>
              </ul>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Account Security</h3>
              <p>You are responsible for maintaining the confidentiality of your password and for all activities that occur under your account. Notify us immediately of any unauthorized use.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">4. Content Submission</h2>
              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Approval Process</h3>
              <p>All submitted business listings and events are subject to review and approval by our administrators. We reserve the right to approve, reject, or remove any submission at our sole discretion.</p>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Content Guidelines</h3>
              <p>You agree that all content you submit:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Is accurate and truthful</li>
                <li>Does not infringe on any third-party intellectual property rights</li>
                <li>Does not contain offensive, defamatory, or illegal content</li>
                <li>Relates to businesses or events in the Martinsville and Henry County area</li>
                <li>Complies with all applicable laws and regulations</li>
              </ul>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Content Ownership</h3>
              <p>You retain ownership of content you submit. By submitting content, you grant Explore MHC a non-exclusive, royalty-free license to display, distribute, and promote your content on the Site and related marketing materials.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">5. Featured Placements & Payments</h2>
              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Paid Features</h3>
              <p>Featured placements are available for purchase and provide enhanced visibility on the Site. Featured status is active for the duration specified at the time of purchase.</p>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Promotional Offers</h3>
              <p>From time to time, we may offer promotional featured placements (such as free featured months for early business listings). These offers are subject to availability and may be modified or discontinued at any time.</p>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Payments</h3>
              <p>All payments are processed securely through Stripe. By making a payment, you agree to Stripe's terms of service. Prices are subject to change without notice.</p>

              <h3 className="text-lg font-semibold text-neutral-800 mt-4 mb-2">Refunds</h3>
              <p>If a paid submission is rejected by our administrators, a full refund will be processed automatically. For other refund requests, please contact us at <a href="mailto:hello@exploremhc.com" className="text-primary hover:underline">hello@exploremhc.com</a>.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">6. Ownership Tags</h2>
              <p>Business owners may self-identify their businesses with ownership tags (e.g., Woman-Owned, Veteran-Owned, Black-Owned). By selecting these tags, you represent that the designation is accurate. Explore MHC does not independently verify ownership designations and is not responsible for inaccurate claims.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">7. Prohibited Activities</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use the Site for any unlawful purpose</li>
                <li>Submit false, misleading, or fraudulent information</li>
                <li>Attempt to gain unauthorized access to the Site or other user accounts</li>
                <li>Interfere with the proper functioning of the Site</li>
                <li>Scrape, harvest, or collect data from the Site without permission</li>
                <li>Use the Site to spam or send unsolicited communications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">8. Disclaimer of Warranties</h2>
              <p>The Site is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Site will be uninterrupted, error-free, or free of viruses or other harmful components.</p>
              <p>We do not endorse, guarantee, or assume responsibility for any business listed on the Site, nor the quality of their products or services.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">9. Limitation of Liability</h2>
              <p>To the fullest extent permitted by law, Explore MHC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of the Site, including but not limited to damages for loss of profits, data, or other intangible losses.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">10. Indemnification</h2>
              <p>You agree to indemnify and hold harmless Explore MHC, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the Site or violation of these Terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">11. Termination</h2>
              <p>We reserve the right to suspend or terminate your account and access to the Site at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">12. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Virginia, without regard to its conflict of law provisions.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">13. Changes to Terms</h2>
              <p>We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Site. Your continued use of the Site after changes are posted constitutes your acceptance of the revised Terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-neutral-900 mt-8 mb-3">14. Contact Us</h2>
              <p>If you have questions about these Terms, please contact us:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Email: <a href="mailto:hello@exploremhc.com" className="text-primary hover:underline">hello@exploremhc.com</a></li>
                <li>Website: <a href="/contact" className="text-primary hover:underline">exploremhc.com/contact</a></li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
